import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import '../styles/InstancesPage.scss';
import {AppDispatch, RootState} from '../store/store';
import {fetchVolumes, setVolumeStatus} from '../store/slices/volumesSlice';
import axiosInstance from "../utility/axiosInstance";
import {handleAxiosError} from "../helpers/handleAxiosError";
import {useToast} from "../components/ToastContext";
import Select, {SingleValue} from 'react-select';
import Modal from "../components/Modal";
import {FaPlus, FaSyncAlt} from 'react-icons/fa';
import {Link} from 'react-router-dom';
import {useConfirm} from "../components/ConfirmContext";
import TrialLock from '../components/TrialLock';
import useAutoOpenCreateModal from '../hooks/useAutoOpenCreateModal';
import { formatConsoleDate } from '../utility/formatConsoleDate';
import useStorageEstimate from '../hooks/useStorageEstimate';

interface OptionType {
    value: string;
    label: string;
}

const HOURS_PER_MONTH = 730;

const VolumesPage: React.FC = () => {
    const {confirm} = useConfirm();
    const {showToast} = useToast();
    const dispatch = useDispatch<AppDispatch>();

    const [attaching, setAttaching] = useState(false);
    const [creating, setCreating] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showAttachModal, setShowAttachModal] = useState(false);
    const [selectedVolume, setSelectedVolume] = useState<string | null>(null);
    const [selectedInstance, setSelectedInstance] = useState<string | null>(null);
    const [showDetachConfirmModal, setShowDetachConfirmModal] = useState(false);
    const [volumeToDetach, setVolumeToDetach] = useState<{ id: string; name: string; attachments: any[] } | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        size: '',
        description: '',
    });

    const creationLocked = useAutoOpenCreateModal(setShowCreateModal);

    // Track volumes currently detaching to poll until they become 'available'
    const [pendingDetachIds, setPendingDetachIds] = useState<Set<string>>(new Set());

    // Track volumes currently attaching to poll until they become 'in-use'
    const [pendingAttachIds, setPendingAttachIds] = useState<Set<string>>(new Set());

    const [currentPage, setCurrentPage] = useState(0);
    const ITEMS_PER_PAGE = 20;

    const volumes = useSelector((state: RootState) => state.volumes.data);
    const volumesStatus = useSelector((state: RootState) => state.volumes.status);
    const instances = useSelector((state: RootState) => state.instances.data);
    const billingForecast = useSelector((state: RootState) => state.billingForecast.forecast);

    const sortedVolumes = [...volumes].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const totalPages = Math.ceil(sortedVolumes.length / ITEMS_PER_PAGE);
    const visibleVolumes = sortedVolumes.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);

    const getAttachedInstanceLabel = (volume: any) => {
        const instanceId = volume.attachments?.[0]?.server_id;
        if (!instanceId) return 'Not attached';
        return instances.find((instance: any) => instance.id === instanceId)?.name || instanceId;
    };

    const selectedVolumeInfo = volumes.find((volume: any) => volume.id === selectedVolume);
    const volumeEstimate = useStorageEstimate('volume', Number(formData.size) || null);
    const getCurrentHourlyRate = (resourceId: string): number | null => {
        const item = billingForecast?.active.find((resource) =>
            resource.service === 'volume' && String(resource.os_resource_id) === String(resourceId)
        );
        const rate = Number(item?.current_hourly_rate);
        return Number.isFinite(rate) ? rate : null;
    };
    const renderCurrentRate = (resourceId: string) => {
        const rate = getCurrentHourlyRate(resourceId);
        if (rate === null) return null;
        const hourly = rate.toFixed(rate > 0 && rate < 0.01 ? 5 : 2);
        return (
            <small style={{ display: 'block', marginTop: 2, color: 'var(--text-muted)' }} title="Current hourly rate before credits">
                Current: £{hourly}/hr · ~£{(rate * HOURS_PER_MONTH).toFixed(2)}/mo
            </small>
        );
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));
    };

    const handleDetachVolume = async (volumeId: string, attachments: any[]) => {
        // Detach confirmation is handled via the dedicated Modal at the bottom of this component.
        setDeletingId(volumeId);
        dispatch(setVolumeStatus({id: volumeId, status: 'detaching'}));

        try {
            if (attachments.length > 0) {
                await axiosInstance.post('/v1/volumes/detach', {
                    volume_id: volumeId,
                    instance_id: attachments[0]?.server_id
                });
                showToast(`VOLUME "${volumeToDetach?.name}" IS BEING DETACHED`, 'info');
                // Start polling this volume until it becomes 'available'
                setPendingDetachIds(prev => {
                    const next = new Set(prev);
                    next.add(volumeId);
                    return next;
                });
            } else {
                showToast(`Volume "${volumeToDetach?.name}" is not attached.`, 'info');
            }
            dispatch(fetchVolumes());
        } catch (err) {
            handleAxiosError(err);
            showToast('Failed to detach volume!', 'error');
            dispatch(setVolumeStatus({id: volumeId, status: 'active'}));
        } finally {
            setDeletingId(null);
            setVolumeToDetach(null);
            setShowDetachConfirmModal(false);
        }
    };

    const handleCreateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name || !formData.size) {
            showToast('Please fill all required fields!', 'warning');
            return;
        }

        setCreating(true);
        try {
            await axiosInstance.post('/v1/volumes/create', {
                name: formData.name,
                size: Number(formData.size),
                description: formData.description,
            });
            setFormData({name: '', size: '', description: ''});
            setShowCreateModal(false);
            showToast('Volume created. You can attach it to a virtual machine when it is ready.', 'success');
            dispatch(fetchVolumes());
        } catch (err) {
            const message = handleAxiosError(err);
            showToast(message || 'Failed to create volume!', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleAttach = async () => {
        if (!selectedVolume || !selectedInstance) return;
        setAttaching(true);

        const volumeBeingAttached = selectedVolume;
        try {
            await axiosInstance.post('/v1/volumes/attach', {
                volume_id: selectedVolume,
                instance_id: selectedInstance,
            });
            showToast('Volume is being attached, waiting for it to become in-use...', 'info');
            setShowAttachModal(false);
            setSelectedInstance(null);
            if (volumeBeingAttached) {
                setPendingAttachIds(prev => {
                    const next = new Set(prev);
                    next.add(volumeBeingAttached);
                    return next;
                });
            }
            dispatch(fetchVolumes());
        } catch (err) {
            handleAxiosError(err);
            showToast('Failed to attach volume!', 'error');
        } finally {
            setAttaching(false);
        }
    };

    const handleDeleteVolume = async (volumeId: string, attachments: any[]) => {
        const proceed = await confirm({
            title: 'Delete Volume',
            message: 'Delete this volume and permanently remove its data? If it is attached, it will be detached first.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;
        setDeletingId(volumeId);
        dispatch(setVolumeStatus({id: volumeId, status: 'deleting'}));
        try {
            if (attachments.length > 0) {
                await axiosInstance.post('/v1/volumes/detach', {
                    volume_id: volumeId,
                    instance_id: attachments[0]?.server_id
                });
            }
            await axiosInstance.delete(`/v1/volumes/delete/${volumeId}`);
        } catch (err) {
            handleAxiosError(err);
            showToast('Failed to delete volume!', 'error');
            dispatch(setVolumeStatus({id: volumeId, status: 'active'}));
        } finally {
            setDeletingId(null);
        }
    };

    const handleVolumeAction = (action: string, volume: any) => {
        if (action === 'add-to-instance') {
            setSelectedVolume(volume.id);
            setSelectedInstance(null);
            setShowAttachModal(true);
        } else if (action === 'detach') {
            if (volume.attachments?.length > 0) {
                setVolumeToDetach({id: volume.id, name: volume.name, attachments: volume.attachments});
                setShowDetachConfirmModal(true);
            } else {
                showToast(`Volume "${volume.name}" is not attached.`, 'info');
            }
        } else if (action === 'delete') {
            handleDeleteVolume(volume.id, volume?.attachments);
        }
    };

    React.useEffect(() => {
        setCurrentPage(0);
    }, [volumes.length]);

    const instanceOptions = instances.map(inst => ({
        value: inst.id,
        label: inst.name
    }));

    const isLoading = volumesStatus === 'loading';

    // Poll volumes while there are pending detaches
    React.useEffect(() => {
        if (pendingDetachIds.size === 0) return;
        const interval = setInterval(() => {
            dispatch(fetchVolumes());
        }, 3000);
        return () => clearInterval(interval);
    }, [pendingDetachIds, dispatch]);

    // Stop polling for a volume once it becomes 'available'
    React.useEffect(() => {
        if (pendingDetachIds.size === 0) return;
        const next = new Set(pendingDetachIds);
        let changed = false;
        for (const v of volumes) {
            if (next.has(v.id) && (v.status?.toLowerCase() === 'available')) {
                next.delete(v.id);
                changed = true;
            }
        }
        if (changed) setPendingDetachIds(next);
    }, [volumes, pendingDetachIds]);

    // Poll volumes while there are pending attaches
    React.useEffect(() => {
        if (pendingAttachIds.size === 0) return;
        const interval = setInterval(() => {
            dispatch(fetchVolumes());
        }, 3000);
        return () => clearInterval(interval);
    }, [pendingAttachIds, dispatch]);

    // Stop polling for a volume once it becomes 'in-use' after attach
    React.useEffect(() => {
        if (pendingAttachIds.size === 0) return;
        const next = new Set(pendingAttachIds);
        let changed = false;
        for (const v of volumes) {
            if (next.has(v.id) && v.status?.toLowerCase() === 'in-use') {
                next.delete(v.id);
                changed = true;
                showToast(`Volume "${v.name}" is now in-use`, 'success');
            }
        }
        if (changed) {
            setPendingAttachIds(next);
            dispatch(fetchVolumes());
        }
    }, [volumes, pendingAttachIds]);

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <h1 className="page-title">Volumes</h1>
                <p className="page-subtitle">
                    Attachable storage for virtual machines.
                </p>
                <a href="https://docs.redu.cloud/Guides/storage-guide" target="_blank" rel="noopener noreferrer"
                   className="docs-link">
                    Documentation →
                </a>
            </div>

            <TrialLock>
                <button
                    className="create-button"
                    onClick={() => setShowCreateModal(true)}
                    title="Create Volume"
                >
                    <FaPlus/>
                    <span>Create Volume</span>
                </button>
            </TrialLock>


            <div className="desktop-view">

                <div className="table-container">
                    <table className="keypairs-table" aria-label="Storage volumes">
                        <thead hidden={!isLoading && sortedVolumes.length === 0}>
                        <tr>
                            <th>Name</th>
                            <th>Size (GB)</th>
                            <th>Status</th>
                            <th>Attached To</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={10} className="loading-state">
                                    <FaSyncAlt className="spin"/> Loading volumes...
                                </td>
                            </tr>
                        ) : sortedVolumes.length ? (
                                visibleVolumes.map((vol) => (
                                        <tr key={vol.id}>
                                            <td className="keypair-name">{vol.name}</td>
                                            <td>
                                                {vol.size} GB
                                                {renderCurrentRate(vol.id)}
                                            </td>
                                            <td>
                                                {vol.status === 'deleting' || vol.status === 'error_deleting' ? (
                                                    <div>
                                                        <div className="deleting-progress"></div>
                                                        <div className="status-label"
                                                             style={{color: '#ef4444'}}>DELETING
                                                        </div>
                                                    </div>
                                                ) : vol.status === 'detaching' || vol.status === 'error_detaching' ? (
                                                    <div>
                                                        <div className="detaching-progress"></div>
                                                        <div className="status-label"
                                                             style={{color: '#f59e0b'}}>
                                                            DETACHING
                                                        </div>
                                                    </div>
                                                ) : vol.status === 'creating' || vol.status === 'downloading' ? (
                                                    <div>
                                                        <div className="building-progress"></div>
                                                        <div className="status-label"
                                                             style={{color: 'var(--status-info)'}}>CREATING
                                                        </div>
                                                    </div>
                                                ) : (
                                                    vol.status
                                                )}
                                            </td>
                                            <td title={vol.attachments?.[0]?.server_id || undefined}>{getAttachedInstanceLabel(vol)}</td>
                                            <td>{formatConsoleDate(vol.created_at)}</td>
                                            <td onClick={(e) => e.stopPropagation()}>

                                                <Select
                                                    options={[
                                                        ...(!vol.attachments?.length ? [{
                                                            value: 'add-to-instance',
                                                            label: 'Attach to Instance'
                                                        }] : []),
                                                        ...(vol.attachments?.length ? [{
                                                            value: 'detach',
                                                            label: 'Detach Volume'
                                                        }] : []),
                                                        {value: 'delete', label: 'Delete Volume'}
                                                    ]}
                                                    onChange={(option) => {
                                                        if (option) {
                                                            handleVolumeAction(option.value, vol);
                                                        }
                                                    }}
                                                    placeholder="Actions"
                                                    isSearchable={false}
                                                    aria-label={`Actions for volume ${vol.name}`}
                                                    menuPortalTarget={document.body}
                                                    isDisabled={attaching || deletingId === vol.id || vol.status === 'creating' || vol.status === 'downloading' || pendingAttachIds.has(vol.id)}
                                                    styles={getActionSelectStyles()}
                                                />
                                            </td>
                                        </tr>
                                    )
                                ))
                            : (
                                <tr>
                                    <td colSpan={6} className="empty-state">
                                        <div className="resource-empty-state">
                                            <strong>No volumes yet</strong>
                                            <span>Create a volume, then attach it to a virtual machine when you need persistent storage.</span>
                                            <span className="resource-empty-state__actions">
                                                <Link to="/category/compute/instances">View virtual machines</Link>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', justifyContent: 'flex-end' }}>
                        <button
                            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                            disabled={currentPage === 0}
                            style={{ border: '1px solid var(--border-1)', borderRadius: '6px', padding: '4px 12px', opacity: currentPage === 0 ? 0.4 : 1, cursor: currentPage === 0 ? 'default' : 'pointer', background: 'transparent', color: 'var(--text-primary)' }}
                        >Prev</button>
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                            {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, sortedVolumes.length)} of {sortedVolumes.length}
                        </span>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={currentPage >= totalPages - 1}
                            style={{ border: '1px solid var(--border-1)', borderRadius: '6px', padding: '4px 12px', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, cursor: currentPage >= totalPages - 1 ? 'default' : 'pointer', background: 'transparent', color: 'var(--text-primary)' }}
                        >Next</button>
                    </div>
                )}
            </div>


            <div className="mobile-view">
                {isLoading ? (
                    <div className="loading-state">
                        <FaSyncAlt className="spin"/>
                        <p>Loading volumes...</p>
                    </div>
                ) : (
                    <div className="cards-container">
                        {sortedVolumes.length > 0 ? (
                            visibleVolumes.map((vol) => (
                                <div key={vol.id} className="keypair-card">
                                    <div className="card-header">
                                        <h3 className="card-title">{vol.name}</h3>
                                        <div style={{width: '100%', marginTop: '12px'}}>
                                            <Select
                                                options={[
                                                    ...(!vol.attachments?.length ? [{
                                                        value: 'add-to-instance',
                                                        label: 'Attach to Instance'
                                                    }] : []),
                                                    ...(vol.attachments?.length ? [{
                                                        value: 'detach',
                                                        label: 'Detach Volume'
                                                    }] : []),
                                                    {value: 'delete', label: 'Delete Volume'}
                                                ]}
                                                onChange={(option) => {
                                                    if (option) {
                                                        handleVolumeAction(option.value, vol);
                                                    }
                                                }}
                                                placeholder="Actions"
                                                isSearchable={false}
                                                aria-label={`Actions for volume ${vol.name}`}
                                                menuPortalTarget={document.body}
                                                isDisabled={attaching || deletingId === vol.id || vol.status === 'creating' || vol.status === 'downloading' || pendingAttachIds.has(vol.id)}
                                                styles={getActionSelectStyles()}
                                            />
                                        </div>
                                    </div>
                                    <div className="card-content">
                                        <div className="fingerprint">
                                            <span className="label">Size:</span>
                                            <span className="value">
                                                {vol.size} GB
                                                {renderCurrentRate(vol.id)}
                                            </span>
                                        </div>
                                        <div className="fingerprint">
                                            <span className="label">Status:</span>
                                            <div className="value">
                                                {vol.status === 'deleting' || vol.status === 'error_deleting' ? (
                                                    <div>
                                                        <div className="deleting-progress"></div>
                                                        <div className="status-label"
                                                             style={{color: '#ef4444'}}>DELETING
                                                        </div>
                                                    </div>
                                                ) : vol.status === 'detaching' || vol.status === 'error_detaching' ? (
                                                    <div>
                                                        <div className="detaching-progress"></div>
                                                        <div className="status-label"
                                                             style={{color: '#f59e0b'}}>
                                                            DETACHING
                                                        </div>
                                                    </div>
                                                ) : vol.status === 'creating' || vol.status === 'downloading' ? (
                                                    <div>
                                                        <div className="building-progress"></div>
                                                        <div className="status-label"
                                                             style={{color: 'var(--status-info)'}}>CREATING
                                                        </div>
                                                    </div>
                                                ) : (
                                                    vol.status
                                                )}
                                            </div>
                                        </div>
                                        <div className="fingerprint">
                                            <span className="label">Attached To:</span>
                                            <span className="value" title={vol.attachments?.[0]?.server_id || undefined}>{getAttachedInstanceLabel(vol)}</span>
                                        </div>
                                        <div className="fingerprint">
                                            <span className="label">Created:</span>
                                            <span
                                                className="value">{formatConsoleDate(vol.created_at)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="resource-empty-state">
                                    <strong>No volumes yet</strong>
                                    <span>Create a volume, then attach it to a virtual machine when you need persistent storage.</span>
                                    <span className="resource-empty-state__actions">
                                        <Link to="/category/compute/instances">View virtual machines</Link>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <Modal
                isOpen={showDetachConfirmModal}
                onClose={() => {
                    setShowDetachConfirmModal(false);
                    setVolumeToDetach(null);
                }}
                title="Detach Volume"
            >
                <div className="create-form">
                    <p>
                        Are you sure you want
                        to <strong>detach</strong> volume <strong>&#34;{volumeToDetach?.name}&#34;</strong>?
                    </p>
                    <p>
                        Stop writes and unmount the device inside the virtual machine before detaching it. Detaching an in-use filesystem can corrupt data.
                    </p>
                    <div className="modal-actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => {
                                setShowDetachConfirmModal(false);
                                setVolumeToDetach(null);
                            }}
                            disabled={deletingId === volumeToDetach?.id}
                        >
                            Cancel
                        </button>
                        <button
                            className="submit-button"
                            onClick={() => volumeToDetach && handleDetachVolume(volumeToDetach.id, volumeToDetach.attachments)}
                            disabled={deletingId === volumeToDetach?.id}
                        >
                            {deletingId === volumeToDetach?.id ? 'Detaching...' : 'Detach Volume'}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={showCreateModal && !creationLocked}
                onClose={() => {
                    setShowCreateModal(false);
                    setFormData({name: '', size: '', description: ''});
                }}
                title="Create Volume"
            >
                <form onSubmit={handleCreateSubmit} className="create-form">
                    <div className="form-group">
                        <label htmlFor="volume-name">Volume name</label>
                        <input
                            id="volume-name"
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="e.g., data-volume-01"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="volume-size">Size (GB)</label>
                        <input
                            id="volume-size"
                            type="number"
                            name="size"
                            min="1"
                            max="2048"
                            value={formData.size}
                            onChange={handleInputChange}
                            required
                            placeholder="1-2048"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="volume-description">Description (optional)</label>
                        <input
                            id="volume-description"
                            type="text"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Backup storage, database, etc."
                        />
                    </div>
                    <p className="help-text">
                        A volume is a separate block device. After it is ready, attach it to a virtual machine, then partition, format, and mount it inside that machine before your application uses it.
                    </p>
                    <p className="help-text">
                        Storage is billed hourly by provisioned GB, rather than data used inside the filesystem.
                    </p>
                    {/* The price on its own line (Milos 2026-10-26); it names the size, so no separate "You selected" */}
                    {volumeEstimate.status === 'loading' && <p className="help-text volume-price">Calculating the estimate…</p>}
                    {volumeEstimate.status === 'ready' && (
                        <p className="help-text volume-price">
                            Estimated at <strong>£{volumeEstimate.estimate.hourly_total.toFixed(5)}/hr · ~£{volumeEstimate.estimate.monthly_total.toFixed(2)}/mo</strong>
                            {' '}for {volumeEstimate.estimate.size_gb} GB at £{volumeEstimate.estimate.unit_hourly_rate.toFixed(5)}/GB/hr, before credits.
                        </p>
                    )}
                    {volumeEstimate.status === 'unavailable' && (
                        <p className="help-text volume-price">Storage pricing is not configured yet. <Link to="/category/user/billing">Billing</Link> remains the authoritative record for rates, usage, and credits.</p>
                    )}
                    <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setShowCreateModal(false)} disabled={creating}>
                            Cancel
                        </button>
                        <button type="submit" className="submit-button" disabled={creating}>
                            {creating ? 'Creating...' : 'Create Volume'}
                        </button>
                    </div>
                </form>
            </Modal>


            <Modal
                isOpen={showAttachModal}
                onClose={() => setShowAttachModal(false)}
                title="Attach Volume"
            >
                <form onSubmit={(e) => {
                    e.preventDefault();
                    handleAttach();
                }} className="create-form">
                    <p className="help-text">
                        {selectedVolumeInfo ? `Attach ${selectedVolumeInfo.name} (${selectedVolumeInfo.size} GB) to make it available as a block device.` : 'Attach the selected volume to make it available as a block device.'} You still need to partition, format, and mount it inside the virtual machine.
                    </p>
                    <div className="form-group">
                        <label htmlFor="volume-instance">Virtual machine</label>
                        <Select
                            inputId="volume-instance"
                            options={instanceOptions}
                            value={instanceOptions.find(opt => opt.value === selectedInstance) || null}
                            onChange={(opt: SingleValue<OptionType>) => setSelectedInstance(opt?.value || null)}
                            placeholder="Select an instance"
                            menuPortalTarget={document.body}
                            styles={getActionSelectStyles()}
                        />
                    </div>
                    <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setShowAttachModal(false)}>
                            Cancel
                        </button>
                        <button type="submit" className="submit-button" disabled={attaching || !selectedInstance}>
                            {attaching ? 'Attaching...' : 'Attach Volume'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default VolumesPage;
